/*
 *   Module Loader
 *   Consumes an array of elements with .js-module classes
 *   to produce an array of module names to be loaded.
 *
 *   Markup must include data-module="['my-module-1', 'my-module-2'"]
 *
 */

/**
 * Modules
 * @type {object} - Collection of properties with async-await fns
 * @prop {function} import - await import of component js
 * @param {comment} webpackChunkName - Generates the chunk filename
 * @param {string} pathToComponent - js to import
 * @returns {module} new example(el) - if js written as class
 * @returns {module} example() - otherwise
 * @if - console log module name loaded
 *
 * example: async () => {
 *   const { default: example } = await import(
 *     / * webpackChunkName: 'example' * /
 *     './components/example'
 *   );
 *   new Example(el);
 *   if (!__PROD__) {
 *     console.log(`%c${key} loaded`, 'color:#000;background:yellow;');
 *   }
 * },
 */

const videoCards = [];
window.videoCards = videoCards || [];

function camelize(text) {
  return text.replace(/-([a-z])/g, function (g) {
    return g[1].toUpperCase();
  });
}

function load(key, el) {
  const modules = {
    // articleFilters
    articleFilters: async () => {
      const { default: articleFilters } = await import(
        /* webpackChunkName: 'articleFilters' */
        '../templates/02-components/article-filters/article-filters'
      );
      new articleFilters(el);

      // Load GA for listings, that have to be loaded AFTER article filters
      const { default: articleListingGa } = await import(
        /* webpackChunkName: 'articleListingGa' */
        '../templates/02-components/article-listing/analytics.article-listing'
      );
      new articleListingGa(el);

      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // brochureDownload
    brochureDownload: async () => {
      const { default: brochureDownload } = await import(
        /* webpackChunkName: 'brochureDownload' */
        './components/brochureDownload'
      );
      new brochureDownload(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // cardBrowser
    cardBrowser: async () => {
      const { default: cardBrowser } = await import(
        /* webpackChunkName: 'cardBrowser' */
        './components/cardBrowser'
      );
      new cardBrowser(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // countdown
    countdown: async () => {
      const { default: countdown } = await import(
        /* webpackChunkName: 'countdown' */
        './components/countdown'
      );
      new countdown(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // countrySelector
    countrySelector: async () => {
      const { default: countrySelector } = await import(
        /* webpackChunkName: 'countrySelector' */
        '../templates/02-components/country-selector/country-selector'
      );
      new countrySelector(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // dealerFind
    dealerFind: async () => {
      const { default: dealerFind } = await import(
        /* webpackChunkName: 'dealerFind' */
        './components/dealerFind'
      );
      new dealerFind(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // dealerLocator
    dealerLocator: async () => {
      const { default: dealerLocator } = await import(
        /* webpackChunkName: 'dealerLocator' */
        './components/dealerLocator'
      );
      new dealerLocator(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // dealerNewProduct
    dealerNewProduct: async () => {
      const { default: dealerNewProduct } = await import(
        /* webpackChunkName: 'dealerNewProduct' */
        './components/dealerNewProduct'
      );
      new dealerNewProduct(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // dealerPrimaryNav
    dealerPrimaryNav: async () => {
      const { default: dealerPrimaryNav } = await import(
        /* webpackChunkName: 'dealerPrimaryNav' */
        './components/dealerPrimaryNav'
      );
      new dealerPrimaryNav(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // faqLanding (Vue)
    faqLanding: async () => {
      const { default: faqLanding } = await import(
        /* webpackChunkName: 'faqLanding' */
        '../app/views/faq-landing/faq-landing'
      );
      new faqLanding(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // faqSpecificationAccordion (Vue)
    faqSpecificationAccordion: async () => {
      const { default: faqSpecificationAccordion } = await import(
        /* webpackChunkName: 'faqSpecificationAccordion' */
        '../app/views/faq-specification-accordion/faq-specification-accordion'
      );
      new faqSpecificationAccordion(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // fuelCalc (Vue)
    fuelCalc: async () => {
      const { default: fuelCalc } = await import(
        /* webpackChunkName: 'fuelCalc' */
        '../app/views/fuel-calc/fuel-calc'
      );
      new fuelCalc(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // headerLogo
    headerLogo: async () => {
      const { default: headerLogo } = await import(
        /* webpackChunkName: 'headerLogo' */
        './components/headerLogo'
      );
      new headerLogo(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // mainNav
    mainNav: async () => {
      const { default: mainNav } = await import(
        /* webpackChunkName: 'mainNav' */
        './components/mainNav'
      );
      new mainNav(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // megaNav - regional
    megaNav: async () => {
      const { default: megaNav } = await import(
        /* webpackChunkName: 'megaNav' */
        './components/megaNav'
      );
      new megaNav(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // mobileNav
    mobileNav: async () => {
      const { default: mobileNav } = await import(
        /* webpackChunkName: 'mobileNav' */
        './components/mobileNav'
      );
      new mobileNav(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // newsEventsList
    newsEventsList: async () => {
      const { default: newsEventsList } = await import(
        /* webpackChunkName: 'newsEventsList' */
        './components/newsEventsList'
      );
      new newsEventsList(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // offers
    offers: async () => {
      const { default: offers } = await import(
        /* webpackChunkName: 'offers' */
        './components/offers'
      );
      new offers(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // offer form
    offerForm: async () => {
      const { default: offerForm } = await import(
        /* webpackChunkName: 'offerForm' */
        './components/offerForm'
      );
      new offerForm(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // tabs
    tabs: async () => {
      const { default: tabs } = await import(
        /* webpackChunkName: 'tabs' */
        './components/tabs'
      );
      new tabs(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // quoteModal
    quotemodal: async () => {
      const { default: quotemodal } = await import(
        /* webpackChunkName: 'quotemodal' */
        './components/quotemodal'
      );
      new quotemodal(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // quote
    quote: async () => {
      const { default: quote } = await import(
        /* webpackChunkName: 'quote' */
        './components/quote'
      );
      new quote(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // videoCard
    videoCard: async () => {
      const { default: videoCard } = await import(
        /* webpackChunkName: 'videoCard' */
        './components/videoCard'
      );
      videoCards.push(new videoCard(el));
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // videoGalleryCarousel
    videoGalleryCarousel: async () => {
      const { default: videoGalleryCarousel } = await import(
        /* webpackChunkName: 'videoGalleryCarousel' */
        './components/videoGalleryCarousel'
      );
      new videoGalleryCarousel(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // videoHero
    videoHero: async () => {
      const { default: videoHero } = await import(
        /* webpackChunkName: 'videoHero' */
        './components/videoHero'
      );
      new videoHero(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // autosuggestSearch (Vue)
    autosuggestSearch: async () => {
      const { default: autosuggestSearch } = await import(
        /* webpackChunkName: 'autosuggestSearch' */
        '../app/views/autosuggest-search/autosuggest-search'
      );
      new autosuggestSearch(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // productListing (Vue)
    productListing: async () => {
      const { default: productListing } = await import(
        /* webpackChunkName: 'productListing' */
        '../app/views/product-listing/product-listing'
      );
      new productListing(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // productSpecification (Vue)
    productSpecification: async () => {
      const { default: productSpecification } = await import(
        /* webpackChunkName: 'productSpecification' */
        '../app/views/product-specification/product-specification'
      );
      new productSpecification(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // productCard (Vue)
    productCard: async () => {
      const { default: productCard } = await import(
        /* webpackChunkName: 'productCard' */
        '../app/views/product-card/product-card'
      );
      new productCard(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
    // relatedProducts (Vue)
    relatedProducts: async () => {
      const { default: relatedProducts } = await import(
        /* webpackChunkName: 'relatedProducts' */
        '../app/views/related-products/related-products'
      );
      new relatedProducts(el);
      if (!__PROD__) {
        console.log(`%c${key} loaded`, 'color: #000; background: yellow;');
      }
    },
  };
  try {
    // return Promise
    return modules[key]();
  } catch (e) {
    console.error(`Module ${key} not found.`);
  }
}

function init(modules) {
  if (!modules) return;
  const moduleList = [];
  modules.forEach((element) => {
    try {
      const moduleNames = element.dataset.module;
      if (moduleNames) {
        const moduleArray = JSON.parse(moduleNames.replace(/'/g, '"'));
        moduleArray.forEach((item) => {
          const module = camelize(item);
          if (moduleList.indexOf(module) === -1) {
            moduleList.push({
              name: module,
              element: element,
            });
          }
        });
      }
    } catch (e) {
      console.error('JS name format incorrect in data attribute:');
    }
  });

  moduleList.forEach((module) => load(module.name, module.element));
}

export default init;
